import {
  reportingPrisma as prisma,
  BookableItemType,
  OrderItemStatus,
} from "client";
import { buildPaymentSettlementExpressions } from "services/virtualOrderStatus";
import {
  getEventTicketsWithPrices,
  getTicketPricesByOids,
  type EventTicketOid,
  type EventTicketWithPrice,
} from "./getEventTicketOids";
import { RESERVED_ORDER_ITEM_STATUSES } from "./countingRules";

type TicketQuantityRow = {
  reserved?: number;
  paid?: number;
  free?: number;
  pending?: number;
  failed?: number;
};

function buildBookablePriceExpression(tickets: EventTicketWithPrice[]) {
  if (tickets.length === 0) {
    return 0;
  }
  return {
    $switch: {
      branches: tickets.map((ticket) => ({
        case: { $eq: ["$ticketId", { $oid: ticket.$oid }] },
        then: ticket.price,
      })),
      default: 0,
    },
  };
}

/**
 * One early-matched aggregation instead of 6 nested Prisma counts.
 * Prefetches ticket prices and skips Ticket/BookableItem $lookups; Payment
 * lookup projects status only.
 */
export const getEventTicketQuantity = async (
  eventId: string,
  /** Prefetched ticket oids — prices are loaded in one follow-up query. */
  ticketOidsArg?: EventTicketOid[]
) => {
  const [tickets, totalTicketsCount] = await Promise.all([
    ticketOidsArg
      ? getTicketPricesByOids(ticketOidsArg)
      : getEventTicketsWithPrices(eventId),
    getTotalTicketCountByEvent(eventId),
  ]);

  const ticketOids: EventTicketOid[] = tickets.map(({ $oid }) => ({ $oid }));

  let reservedTicketCount = 0;
  let paidTicketCount = 0;
  let freeSoldTicketCount = 0;
  let pendingTicketCount = 0;
  let failedPaymentTicketCount = 0;

  if (ticketOids.length > 0) {
    const raw = await prisma.orderItem.aggregateRaw({
      pipeline: [
        {
          $match: {
            ticketId: { $in: ticketOids },
            status: {
              $in: [
                ...RESERVED_ORDER_ITEM_STATUSES,
                OrderItemStatus.CONFIRMED,
              ],
            },
          },
        },
        {
          $lookup: {
            from: "Order",
            let: { oid: "$orderId" },
            pipeline: [
              { $match: { $expr: { $eq: ["$_id", "$$oid"] } } },
              { $project: { total: 1 } },
            ],
            as: "order",
          },
        },
        { $unwind: "$order" },
        {
          $lookup: {
            from: "Payment",
            let: { oid: "$orderId" },
            pipeline: [
              { $match: { $expr: { $eq: ["$orderId", "$$oid"] } } },
              { $project: { status: 1 } },
            ],
            as: "payments",
          },
        },
        {
          $addFields: {
            bookablePrice: buildBookablePriceExpression(tickets),
            paymentStatuses: {
              $map: {
                input: "$payments",
                as: "p",
                in: "$$p.status",
              },
            },
            isReserved: {
              $in: ["$status", RESERVED_ORDER_ITEM_STATUSES],
            },
            isConfirmed: {
              $eq: ["$status", OrderItemStatus.CONFIRMED],
            },
          },
        },
        {
          $addFields: {
            isFreeTicket: {
              $cond: {
                if: { $ne: ["$total", null] },
                then: { $lte: ["$total", 0] },
                else: { $lte: ["$bookablePrice", 0] },
              },
            },
            // Legacy: unset item total + free order (no PAID) — mirrors freeOrderWhere.
            isFreeViaOrder: {
              $and: [
                {
                  $or: [
                    { $eq: ["$total", null] },
                    { $eq: [{ $type: "$total" }, "missing"] },
                  ],
                },
                {
                  $or: [
                    { $eq: ["$order.total", 0] },
                    { $eq: ["$order.total", null] },
                    { $eq: [{ $type: "$order.total" }, "missing"] },
                  ],
                },
              ],
            },
            isPaidTicket: {
              $cond: {
                if: { $ne: ["$total", null] },
                then: { $gt: ["$total", 0] },
                else: { $gt: ["$bookablePrice", 0] },
              },
            },
            ...buildPaymentSettlementExpressions("$paymentStatuses"),
          },
        },
        {
          $group: {
            _id: null,
            reserved: {
              $sum: { $cond: ["$isReserved", 1, 0] },
            },
            paid: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      "$isConfirmed",
                      { $eq: ["$anyPaid", true] },
                      { $eq: ["$isPaidTicket", true] },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
            free: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      "$isConfirmed",
                      {
                        $or: [
                          { $eq: ["$isFreeTicket", true] },
                          {
                            $and: [
                              { $eq: ["$isFreeViaOrder", true] },
                              { $eq: ["$anyPaid", false] },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
            pending: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      "$isConfirmed",
                      { $eq: ["$hasActiveAwaitingPayment", true] },
                      { $eq: ["$anyPaid", false] },
                      { $eq: ["$isPaidTicket", true] },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
            failed: {
              $sum: {
                $cond: [
                  {
                    $and: [
                      "$isConfirmed",
                      { $eq: ["$isPaidTicket", true] },
                      { $eq: ["$hasOnlySupersededPayments", true] },
                    ],
                  },
                  1,
                  0,
                ],
              },
            },
          },
        },
      ],
    });

    const row = (Array.isArray(raw) ? raw[0] : null) as
      | TicketQuantityRow
      | null
      | undefined;
    reservedTicketCount = Number(row?.reserved ?? 0);
    paidTicketCount = Number(row?.paid ?? 0);
    freeSoldTicketCount = Number(row?.free ?? 0);
    pendingTicketCount = Number(row?.pending ?? 0);
    failedPaymentTicketCount = Number(row?.failed ?? 0);
  }

  const consumedCount =
    reservedTicketCount +
    paidTicketCount +
    freeSoldTicketCount +
    pendingTicketCount +
    failedPaymentTicketCount;
  const availableTicketCount = Math.max(0, totalTicketsCount - consumedCount);

  const toPercent = (count: number) =>
    count && totalTicketsCount
      ? Math.round((count / totalTicketsCount) * 100)
      : 0;

  return [
    {
      title: "Reserved",
      metric: reservedTicketCount,
      value: toPercent(reservedTicketCount),
      target: totalTicketsCount,
    },
    {
      title: "Paid",
      metric: paidTicketCount,
      value: toPercent(paidTicketCount),
      target: totalTicketsCount,
    },
    {
      title: "Free",
      metric: freeSoldTicketCount,
      value: toPercent(freeSoldTicketCount),
      target: totalTicketsCount,
    },
    {
      title: "Pending",
      metric: pendingTicketCount,
      value: toPercent(pendingTicketCount),
      target: totalTicketsCount,
    },
    {
      title: "Failed payment",
      metric: failedPaymentTicketCount,
      value: toPercent(failedPaymentTicketCount),
      target: totalTicketsCount,
    },
    {
      title: "Available",
      metric: availableTicketCount,
      value: toPercent(availableTicketCount),
      target: totalTicketsCount,
    },
  ];
};

export const getTotalTicketCountByEvent = async (eventId: string) => {
  const ticketCount = await prisma.bookableItem.aggregate({
    where: {
      type: BookableItemType.TICKET,
      ticket: {
        eventId,
        OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
      },
    },
    _sum: { quantity: true },
  });
  return ticketCount._sum?.quantity || 0;
};
